import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../../../authContext/context';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Badge from 'react-bootstrap/Badge';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from '../../../../lib/axiosIntercepter';

export default function ViewEmailTemplate() {
  const [loading, setLoading] = useState(true);
  const [templateData, setTemplateData] = useState(null);
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();

  // Fetch template data
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await axiosInstance.get(`private/email-templates/${id}`);
        const data = response.data;

        if (data.status === 200) {
          setTemplateData(data.data);
        } else {
          throw new Error(data.message || 'Failed to fetch template');
        }
      } catch (error) {
        console.error('Error fetching template:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error Loading Template',
          text: error.message || 'Failed to load template data'
        }).then(() => {
          navigate('/emailtemplates');
        });
      } finally {
        setLoading(false);
      }
    };

    if (id && authCtx.token) {
      fetchTemplate();
    }
  }, [id, authCtx.token, navigate]);

  if (loading) {
    return (
      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>View Email Template</h1>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!templateData) {
    return (
      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Email Template Not Found</h1>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/emailtemplates')}
          className='mt-3'
        >
          Back to Templates
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>View Email Template</h1>
        <div className='header-right-btn'>
          <Stack spacing={2} direction="row">
            <Button 
              variant="outlined" 
              onClick={() => navigate('/emailtemplates')}
              startIcon={<ArrowBackIcon />}
              className='text-capitalize'
              style={{ borderColor: '#6c757d', color: '#6c757d' }}
            >
              Back to List
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate(`/emailtemplates/edit/${id}`)}
              startIcon={<EditIcon />}
              className='red-bg red-button text-capitalize'
            >
              Edit Template
            </Button>
          </Stack>
        </div>
      </div>

      <div className='page-top-formview mb-3'>
        <Row className="mb-4">
          {/* Basic Information */}
          <Col lg={6} className="mb-4">
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0">Basic Information</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>Template Name:</strong>
                  <div className="mt-1">{templateData.name}</div>
                </div>
                
                {templateData.description && (
                  <div className="mb-3">
                    <strong>Description:</strong>
                    <div className="mt-1">{templateData.description}</div>
                  </div>
                )}
                
                <div className="mb-3">
                  <strong>Product:</strong>
                  <div className="mt-1">
                    <Badge bg="primary">{templateData.productId?.title || 'N/A'}</Badge>
                  </div>
                </div>
                
                <div className="mb-3">
                  <strong>Email Theme:</strong>
                  <div className="mt-1">
                    {templateData.emailThemeId ? (
                      <span 
                        style={{
                          display: 'inline-block',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          backgroundColor: templateData.emailThemeId.headerColor || '#6c757d',
                          color: '#fff',
                          fontSize: '0.9em'
                        }}
                      >
                        {templateData.emailThemeId.name} ({templateData.emailThemeId.type})
                      </span>
                    ) : (
                      <Badge bg="secondary">No Theme</Badge>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <strong>Email Subject:</strong>
                  <div className="mt-1">{templateData.subject}</div>
                </div>

                <div className="mb-0">
                  <strong>Status:</strong>
                  <div className="mt-1">
                    <Badge bg={templateData.isActive ? 'success' : 'danger'}>
                      {templateData.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Email Preview */}
          <Col lg={6} className="mb-4">
            <Card style={{ height: '100%' }}>
              <Card.Header className="bg-light">
                <h5 className="mb-0">Email Preview</h5>
              </Card.Header>
              <Card.Body>
                {templateData.emailThemeId ? (
                  <div
                    style={{
                      backgroundColor: templateData.emailThemeId.backgroundColor || '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    {/* Mock Email Container */}
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '6px',
                        padding: '20px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      {/* Email Subject as Header */}
                      <h4
                        style={{
                          color: templateData.emailThemeId.headerColor || '#dc2626',
                          fontWeight: 'bold',
                          marginBottom: '16px',
                          textAlign: 'center',
                          borderBottom: `2px solid ${templateData.emailThemeId.headerColor || '#dc2626'}`,
                          paddingBottom: '12px'
                        }}
                      >
                        {templateData.subject}
                      </h4>

                      {/* Email Content */}
                      <div 
                        style={{ 
                          marginBottom: '20px',
                          color: templateData.emailThemeId.textColor || '#333333',
                          whiteSpace: 'pre-line',
                          lineHeight: '1.6'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: templateData.content.replace(/\n/g, '<br/>') 
                        }}
                      />

                      {/* Template Info */}
                      <div
                        style={{
                          marginTop: '20px',
                          paddingTop: '16px',
                          borderTop: '1px solid #e5e7eb',
                          fontSize: '0.85em',
                          color: '#6c757d'
                        }}
                      >
                        <div>Template: {templateData.name}</div>
                        <div>Theme: {templateData.emailThemeId.name} ({templateData.emailThemeId.type})</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6c757d',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    No email theme selected for preview
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Content Details */}
        <Row>
          <Col lg={12}>
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0">Email Content</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>Raw Content:</strong>
                  <div 
                    className="mt-2 p-3" 
                    style={{ 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '0.9em',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {templateData.content}
                  </div>
                </div>

                {templateData.createdAt && (
                  <div className="mb-3">
                    <strong>Created:</strong>
                    <div className="mt-1">
                      {new Date(templateData.createdAt).toLocaleString()}
                    </div>
                  </div>
                )}

                {templateData.updatedAt && (
                  <div className="mb-0">
                    <strong>Last Updated:</strong>
                    <div className="mt-1">
                      {new Date(templateData.updatedAt).toLocaleString()}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}